<template>
  <aside class="results" aria-label="Results">
    <!-- Covers the panel with the AVA logo loader while the verification VM
         is being fetched. Shares the IS_LOADING signal with ContextPanel —
         both consume the same VM, so they flip together. -->
    <AvaLoader v-if="isLoading" overlay />
    <PanelHeader title="RESULTS" />

    <!-- No v-if: GET_POINT_OVERALL_DISPLAY always returns an object (it reads
         '—' / '0 of 0 points complete' before anything is captured), unlike
         the deprecated GET_OVERALL_DISPLAY it replaced. -->
    <OverallStatus :overall="overall" />

    <div class="check-list">
      <p v-if="!points.length" class="results__empty">
        No verification points captured yet.
      </p>

      <!-- Configuration happens in Context before capture. Results stays a
           review surface, and every comment belongs to the point card that
           emitted it. -->
      <!-- The card renders positions in SITE metres and is a store-free leaf,
           so the panel supplies the transform it needs to get there. -->
      <PointResultCard
        v-for="point in points"
        :key="point.id"
        :point="point"
        :unit-transform="unitTransform"
        @comment="onPointComment(point.id, $event)"
        @remove-evidence="onRemoveEvidence(point.id, $event)"
      />
    </div>

    <ToleranceRefStrip :tol-ref="toleranceRefStrip" @view="onViewRef" />

    <ActionsStack
      :report-button-label="reportButtonLabel"
      :capture-disabled="captureDisabled"
      :capture-state="captureState"
      :capture-title="captureTitle"
      @capture-evidence="onCaptureEvidence"
      @complete-check="onCompleteCheck"
      @generate-report="onGenerateReport"
    />
  </aside>
</template>

<script>
/**
 * Right-hand results panel (AVA-475 Task 6 Step 4).
 *
 * Rewired from the deprecated check-centric chain (GET_OVERALL_DISPLAY /
 * GET_CHECKS / GET_ACTIVE_CHECK_ID + CheckRow) onto the point model:
 * GET_POINTS_WITH_RESULTS + GET_POINT_OVERALL_DISPLAY + PointResultCard.
 * Those three getters are no longer referenced anywhere here, which is what
 * lets Task 11 delete them; CheckRow.vue itself is left on disk for that task.
 *
 * Applied tolerance is configured in Context above the point list. Comments
 * are point-owned and emitted by each PointResultCard.
 *
 * OverallStatus takes an OBJECT ({ short, className, progress, failing,
 * badge }). GET_OVERALL_STATUS is a bare string and binding it would render
 * `overall-value--undefined` with no console error, so the panel binds
 * GET_POINT_OVERALL_DISPLAY, which is that mapping done once in the store.
 *
 * Panel role: it maps the getters and dispatches; PointResultCard is a
 * prop-in / comment-out leaf.
 */
import { mapGetters } from 'vuex';
import { log as debugLog } from '@/utils/debugLogger';
import { AvaLoader } from '@/components/loaders';
import PanelHeader from '@/features/Verification/components/shared/PanelHeader.vue';
import OverallStatus from '@/features/Verification/components/results/OverallStatus.vue';
import PointResultCard from '@/features/Verification/components/results/PointResultCard.vue';
import ToleranceRefStrip from '@/features/Verification/components/results/ToleranceRefStrip.vue';
import ActionsStack from '@/features/Verification/components/results/ActionsStack.vue';
import { REPORT_EVENTS } from '@/features/Verification/utils/captureEvents';
import { EventBus } from '@/event-bus';

/** How long the "Captured ✓" flash stays up before reverting to idle. */
const CAPTURE_FLASH_MS = 1500;

/** Longer — a failure has to survive a glance away from the panel. */
const CAPTURE_FAIL_MS = 4000;

export default {
  name: 'ResultsPanel',
  components: {
    AvaLoader,
    PanelHeader,
    OverallStatus,
    PointResultCard,
    ToleranceRefStrip,
    ActionsStack,
  },
  data() {
    return {
      captureState: 'idle',
      captureFlashTimer: null,
    };
  },
  beforeDestroy() {
    // The flash timer outlives the panel on a route leave otherwise, and fires
    // a setState on a destroyed component.
    clearTimeout(this.captureFlashTimer);
  },
  computed: {
    ...mapGetters('VERIFICATION', {
      overall: 'GET_POINT_OVERALL_DISPLAY',
      points: 'GET_POINTS_WITH_RESULTS',
      toleranceRefStrip: 'GET_TOLERANCE_REF_STRIP',
      reportButtonLabel: 'GET_REPORT_BUTTON_LABEL',
      isLoading: 'IS_LOADING',
      activePoint: 'GET_ACTIVE_POINT',
      viewerStatus: 'GET_VIEWER_STATUS',
      // Mapped HERE and passed down, rather than read inside PointResultCard:
      // the card is a store-free leaf by contract, and the panel is already the
      // component that talks to Vuex on its behalf.
      unitTransform: 'GET_UNIT_TRANSFORM',
    }),

    /**
     * Gated on 'ready', NOT on the looser `viewerReady` convention used by the
     * status bar (which counts 'isolating' as ready). During 'isolating' the
     * model is up but the crop / scan pre-flight has not applied, so a
     * screenshot shows the whole building rather than the isolated support —
     * a picture that looks like evidence and proves nothing.
     */
    captureDisabled() {
      return !this.activePoint || this.viewerStatus !== 'ready';
    },

    captureTitle() {
      if (!this.activePoint) return 'Select a verification point to attach evidence to';
      if (this.viewerStatus !== 'ready') return 'Waiting for the viewer to finish preparing the view';
      return 'Screenshot the viewer exactly as it looks now';
    },
  },
  methods: {
    onPointComment(id, comment) {
      this.$store.dispatch('VERIFICATION/setPointComment', { id, comment });
    },
    onViewRef() {
      debugLog('[ResultsPanel] view tolerance ref');
    },
    onRemoveEvidence(pointId, evidenceId) {
      this.$store.dispatch('VERIFICATION/removePointEvidence', { pointId, evidenceId });
    },

    /**
     * The store owns the capture; this only reflects its outcome in the button.
     * The success flash is driven by the dispatch RESOLVING with true, not by
     * the click — a capture that failed must not congratulate the surveyor on
     * evidence that was never stored.
     */
    async onCaptureEvidence() {
      if (this.captureDisabled || this.captureState === 'busy') return;
      clearTimeout(this.captureFlashTimer);
      this.captureState = 'busy';
      const stored = await this.$store.dispatch('VERIFICATION/CAPTURE_POINT_EVIDENCE');
      debugLog('[ResultsPanel] capture-evidence finished', { stored });
      // A failure is REPORTED, not swallowed back to idle. A click that appears
      // to do nothing reads as "captured" to the surveyor, and nothing was.
      this.captureState = stored ? 'done' : 'failed';
      this.captureFlashTimer = setTimeout(() => {
        this.captureState = 'idle';
      }, stored ? CAPTURE_FLASH_MS : CAPTURE_FAIL_MS);
    },
    onCompleteCheck() {
      debugLog('[ResultsPanel] complete-check clicked');
    },
    onGenerateReport() {
      debugLog('[ResultsPanel] generate-report clicked — opening preview');
      EventBus.$emit(REPORT_EVENTS.OPEN_PREVIEW);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.results {
  background: $vf-panel;
  border-left: 1px solid $vf-line;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  // Anchor for <AvaLoader overlay> (position:absolute; inset:0).
  position: relative;

  &__empty {
    margin: 0;
    padding: 12px 4px;
    font-size: 11.5px;
    color: $vf-ink-3;
  }

}

// Shrink the logo 20% inside panel loaders without touching the backdrop —
// scaling the container keeps the backdrop full-sized so the panel stays
// fully covered while loading. Mirrors ContextPanel for consistency.
::v-deep .ava-loader__container {
  transform: scale(0.8);
}

.check-list {
  padding: 8px 12px;
  overflow-y: auto;
  flex: 1;
}
</style>
